import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2Icon, InstagramIcon, Loader2Icon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { site, formEndpoint } from '../data/site';
import { formations } from '../data/formations';
import { useSeo } from '../hooks/useSeo';
type Statut = 'idle' | 'sending' | 'sent' | 'error';
interface FormState {
  nom: string;
  structure: string;
  email: string;
  telephone: string;
  profil: string;
  formation: string;
  objet: string;
  message: string;
  consent: boolean;
}
const profils = ['Entreprise', 'Dirigeant·e', 'Commerçant·e', 'Indépendant·e', 'Salarié·e', 'Manager', 'Créateur·rice / porteur de projet', 'Association', 'Autre'];
const objets = [{
  value: 'devis',
  label: 'Demander un devis'
}, {
  value: 'rappel',
  label: 'Être recontacté·e'
}, {
  value: 'info',
  label: 'Demander une information'
}];
export function Contact() {
  useSeo({
    title: 'Contact — Zillenium Academy, formation professionnelle à Joigny (89)',
    description: 'Contactez Zillenium Academy, organisme de formation certifié Qualiopi : 25 rue Gabriel Cortel, 89300 Joigny — administratif@zilleniumacademy.fr — 09 83 49 01 81.'
  });
  const [params] = useSearchParams();
  const [statut, setStatut] = useState<Statut>('idle');
  const [erreurs, setErreurs] = useState<Partial<Record<keyof FormState, string>>>({});
  const [form, setForm] = useState<FormState>({
    nom: '',
    structure: '',
    email: '',
    telephone: '',
    profil: profils[0],
    formation: params.get('formation') ?? '',
    objet: params.get('objet') ?? 'devis',
    message: '',
    consent: false
  });
  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(f => ({
      ...f,
      [key]: value
    }));
    setErreurs(e => ({
      ...e,
      [key]: undefined
    }));
  }
  function validate(): boolean {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (form.nom.trim().length < 2) next.nom = 'Merci d’indiquer votre nom.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email)) next.email = 'Adresse e-mail invalide.';
    if (form.message.trim().length < 10) next.message = 'Décrivez votre besoin en quelques mots (10 caractères minimum).';
    if (!form.consent) next.consent = 'Votre accord est nécessaire pour traiter la demande.';
    setErreurs(next);
    return Object.keys(next).length === 0;
  }
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setStatut('sending');
    const formationTitre = formations.find(f => f.slug === form.formation)?.titre ?? 'Non précisée';
    const objetLabel = objets.find(o => o.value === form.objet)?.label ?? 'Demande';
    try {
      const reponse = await fetch(formEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          _subject: `${objetLabel} — ${formationTitre}`,
          _template: 'table',
          _captcha: 'false',
          Objet: objetLabel,
          Nom: form.nom,
          Structure: form.structure || 'Non précisée',
          Email: form.email,
          Telephone: form.telephone || 'Non précisé',
          Profil: form.profil,
          Formation: formationTitre,
          Message: form.message,
          Origine: `Formulaire ${site.site}`
        })
      });
      if (!reponse.ok) throw new Error('Envoi impossible');
      setStatut('sent');
    } catch {
      setStatut('error');
    }
  }
  const inputBase = 'w-full rounded-lg border bg-white px-3.5 py-3 text-[15px] text-ink outline-none transition-colors duration-150 ease-out placeholder:text-ink-faint focus:border-accent-500';
  return <>
      <section className="border-b border-surface-line bg-surface-soft" data-id="element-160" data-mp="1ppj94vo8y290">
        <div className="mx-auto max-w-content px-5 py-14 lg:px-8 lg:py-16" data-id="element-161" data-mp="1prnivu8ttvh7">
          <h1 className="max-w-3xl text-4xl font-extrabold leading-tight text-ink md:text-[46px]" data-id="element-162" data-mp="1ts7g0xtegz3c">
            Contact, devis & inscription
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-ink-muted" data-id="element-163" data-mp="256soxh4mm4fo">
            Décrivez votre besoin : nous vous rappelons pour analyser vos objectifs, le profil des
            bénéficiaires et les éventuels besoins d’adaptation, puis nous vous transmettons un
            programme et un devis.
          </p>
        </div>
      </section>

      <div className="mx-auto grid max-w-content gap-12 px-5 py-14 lg:grid-cols-[1.5fr_1fr] lg:px-8" data-id="element-164" data-mp="2susmp10cuv3r">
        <div data-id="element-165" data-mp="2nvpej7p2w8rk">
          {statut === 'sent' ? <div className="rounded-xl border border-surface-line bg-white p-8 shadow-card" role="status" aria-live="polite" data-id="element-166" data-mp="2ixxpqmjqd2rl">
              <CheckCircle2Icon size={34} className="text-accent-500" aria-hidden data-id="element-167" data-mp="17k1ywgjgr5kj" />
              <h2 className="mt-4 text-2xl font-bold text-ink" data-id="element-168" data-mp="13hynfrwso0z9">Merci, votre message a bien été transmis</h2>
              <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-ink-muted" data-id="element-169" data-mp="3f5kawopziial">
                Merci {form.nom.split(' ')[0]}, votre demande vient d’arriver dans notre boîte mail.
                Nous revenons vers vous à l’adresse <strong data-id="element-170" data-mp="3o4xkrii0t8vu">{form.email}</strong> pour analyser votre
                besoin, puis vous transmettre un programme et un devis. Pour une urgence, appelez le{' '}
                {site.telephone}.
              </p>
              <div className="mt-7 flex flex-wrap gap-3" data-id="element-171" data-mp="1pw15zoeo55b">
                <Link to="/formations" className="rounded-lg bg-brand-900 px-5 py-3 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-800" data-id="element-172" data-mp="6oz6zmc7mfec">
                  Voir nos formations
                </Link>
                <button type="button" onClick={() => {
              setStatut('idle');
              setForm(f => ({
                ...f,
                message: ''
              }));
            }} className="rounded-lg border border-surface-line px-5 py-3 text-sm font-semibold text-brand-800 transition-colors duration-150 ease-out hover:bg-surface-soft" data-id="element-173" data-mp="2cip4rz7tsqpr">
                  Envoyer une autre demande
                </button>
              </div>
            </div> : <form onSubmit={handleSubmit} noValidate className="grid gap-5 sm:grid-cols-2" data-id="element-174" data-mp="3oszrgwlbpr8v">
              {statut === 'error' && <p role="alert" className="sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[15px] leading-relaxed text-red-700" data-id="element-175" data-mp="7k2yabo686y8">
                  L’envoi n’a pas abouti. Réessayez dans un instant, ou écrivez-nous directement à{' '}
                  <a href={`mailto:${site.email}`} className="font-semibold underline" data-id="element-176" data-mp="cf07pjpd4ibn">
                    {site.email}
                  </a>{' '}
                  ou au {site.telephone}.
                </p>}

              <fieldset className="sm:col-span-2" data-id="element-177" data-mp="14zdsuvlia6o5">
                <legend className="mb-2.5 text-sm font-semibold text-ink" data-id="element-178" data-mp="14znlkqxxqrxj">Votre demande</legend>
                <div className="flex flex-wrap gap-2" data-id="element-179" data-mp="19xp2p6bswh6d">
                  {objets.map(o => <label key={o.value} className={`cursor-pointer rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-150 ease-out ${form.objet === o.value ? 'border-accent-500 bg-accent-500 text-white' : 'border-surface-line bg-white text-ink-muted hover:border-accent-400'}`} data-id="element-180" data-mp="v0eq1tnf9592">
                      <input type="radio" name="objet" value={o.value} checked={form.objet === o.value} onChange={e => update('objet', e.target.value)} className="sr-only" data-id="element-181" data-mp="h8qje5vxz8zh" />
                      {o.label}
                    </label>)}
                </div>
              </fieldset>

              <div data-id="element-182" data-mp="dsovmujnpvkw">
                <label htmlFor="nom" className="mb-1.5 block text-sm font-semibold text-ink" data-id="element-183" data-mp="ds5o87rmupl4">
                  Nom et prénom <span className="text-accent-600" data-id="element-184" data-mp="8udmkm16rp3q">*</span>
                </label>
                <input id="nom" value={form.nom} onChange={e => update('nom', e.target.value)} aria-invalid={Boolean(erreurs.nom)} aria-describedby={erreurs.nom ? 'nom-error' : undefined} className={`${inputBase} ${erreurs.nom ? 'border-red-400' : 'border-surface-line'}`} placeholder="Camille Durand" data-id="element-185" data-mp="4sinlo6q9nt4" />
                {erreurs.nom && <p id="nom-error" className="mt-1.5 text-sm text-red-600" data-id="element-186" data-mp="3d7sjgek0xwsg">
                    {erreurs.nom}
                  </p>}
              </div>

              <div data-id="element-187" data-mp="1spi5d0ddywxm">
                <label htmlFor="structure" className="mb-1.5 block text-sm font-semibold text-ink" data-id="element-188" data-mp="1ss4cx00a1cfu">
                  Entreprise / structure
                </label>
                <input id="structure" value={form.structure} onChange={e => update('structure', e.target.value)} className={`${inputBase} border-surface-line`} placeholder="Nom de votre structure" data-id="element-189" data-mp="21qzf8107a2dv" />
              </div>

              <div data-id="element-190" data-mp="1dyixjrkdkcf3">
                <label htmlFor="email" className="mb-1.5 block text-sm font-semibold text-ink" data-id="element-191" data-mp="1j089d9ff8u48">
                  E-mail <span className="text-accent-600" data-id="element-192" data-mp="1nr6tkk3j6ccr">*</span>
                </label>
                <input id="email" type="email" value={form.email} onChange={e => update('email', e.target.value)} aria-invalid={Boolean(erreurs.email)} aria-describedby={erreurs.email ? 'email-error' : undefined} className={`${inputBase} ${erreurs.email ? 'border-red-400' : 'border-surface-line'}`} placeholder="camille@exemple.fr" data-id="element-193" data-mp="qbarh7f4gzc0" />
                {erreurs.email && <p id="email-error" className="mt-1.5 text-sm text-red-600" data-id="element-194" data-mp="13ttlgd2dr28h">
                    {erreurs.email}
                  </p>}
              </div>

              <div data-id="element-195" data-mp="b5xqbcsglidt">
                <label htmlFor="telephone" className="mb-1.5 block text-sm font-semibold text-ink" data-id="element-196" data-mp="b5eb43u7183h">
                  Téléphone
                </label>
                <input id="telephone" type="tel" value={form.telephone} onChange={e => update('telephone', e.target.value)} className={`${inputBase} border-surface-line`} placeholder="06 12 34 56 78" data-id="element-197" data-mp="g3fqoeseupa7" />
              </div>

              <div data-id="element-198" data-mp="25q90e1oas295">
                <label htmlFor="profil" className="mb-1.5 block text-sm font-semibold text-ink" data-id="element-199" data-mp="25pfs2w9ox4ow">
                  Votre profil
                </label>
                <select id="profil" value={form.profil} onChange={e => update('profil', e.target.value)} className={`${inputBase} border-surface-line`} data-id="element-200" data-mp="1wp16rwwq7ple">
                  {profils.map(p => <option key={p} value={p} data-id="element-201" data-mp="1i0eqnsz9ytt4">
                      {p}
                    </option>)}
                </select>
              </div>

              <div data-id="element-202" data-mp="1dzbephkclhx6">
                <label htmlFor="formation" className="mb-1.5 block text-sm font-semibold text-ink" data-id="element-203" data-mp="1dwfe1lh48f51">
                  Formation concernée
                </label>
                <select id="formation" value={form.formation} onChange={e => update('formation', e.target.value)} className={`${inputBase} border-surface-line`} data-id="element-204" data-mp="13zaqbh7mgbm4">
                  <option value="" data-id="element-205" data-mp="uczxou38j2c6">Je ne sais pas encore</option>
                  {formations.map(f => <option key={f.slug} value={f.slug} data-id="element-206" data-mp="gdql04e8mqcy">
                      {f.titre}
                    </option>)}
                </select>
              </div>

              <div className="sm:col-span-2" data-id="element-207" data-mp="184ek7awcplnz">
                <label htmlFor="message" className="mb-1.5 block text-sm font-semibold text-ink" data-id="element-208" data-mp="185pnl9lvwrek">
                  Votre besoin <span className="text-accent-600" data-id="element-209" data-mp="1d2znjj6wglgn">*</span>
                </label>
                <textarea id="message" rows={5} value={form.message} onChange={e => update('message', e.target.value)} aria-invalid={Boolean(erreurs.message)} aria-describedby={erreurs.message ? 'message-error' : undefined} className={`${inputBase} resize-y ${erreurs.message ? 'border-red-400' : 'border-surface-line'}`} placeholder="Objectifs, nombre de participants, format souhaité, échéance, besoins d’adaptation éventuels…" data-id="element-210" data-mp="zb1gddwr0zn4" />
                {erreurs.message && <p id="message-error" className="mt-1.5 text-sm text-red-600" data-id="element-211" data-mp="3laky42nuutjt">
                    {erreurs.message}
                  </p>}
              </div>

              <div className="sm:col-span-2" data-id="element-212" data-mp="2smgq3ahta4dy">
                <label className="flex items-start gap-3 text-sm leading-relaxed text-ink-muted" data-id="element-213" data-mp="2snqra8jynsot">
                  <input type="checkbox" checked={form.consent} onChange={e => update('consent', e.target.checked)} aria-invalid={Boolean(erreurs.consent)} className="mt-0.5 h-4 w-4 rounded border-surface-line text-accent-600 focus:ring-accent-500" data-id="element-214" data-mp="37khgxq3u1q18" />
                  <span data-id="element-215" data-mp="2nj7rofszqh0c">
                    J’accepte que mes données soient utilisées pour traiter ma demande de formation.
                    Elles ne sont ni cédées ni revendues.
                  </span>
                </label>
                {erreurs.consent && <p className="mt-1.5 text-sm text-red-600" data-id="element-216" data-mp="2wi1lxziumvaq">{erreurs.consent}</p>}
              </div>

              <div className="sm:col-span-2" data-id="element-217" data-mp="31jqzbltjnl1z">
                <button type="submit" disabled={statut === 'sending'} className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent-500 px-6 py-3.5 text-base font-semibold text-white transition-colors duration-150 ease-out hover:bg-accent-600 disabled:opacity-70" data-id="element-218" data-mp="36i29xyrnxzl9">
                  {statut === 'sending' && <Loader2Icon size={18} className="animate-spin" aria-hidden data-id="element-219" data-mp="3p8jvnje2vux4" />}
                  {statut === 'sending' ? 'Envoi en cours…' : 'Envoyer ma demande'}
                </button>
              </div>
            </form>}
        </div>

        <aside className="space-y-4" data-id="element-220" data-mp="83b5d0mi1nyg">
          <div className="rounded-xl border border-surface-line bg-surface-soft p-6" data-id="element-221" data-mp="36b556cbzff9">
            <h2 className="text-lg font-bold text-ink" data-id="element-222" data-mp="3uccbx2p9jmv3">{site.nom}</h2>
            <ul className="mt-4 space-y-3 text-[15px]" data-id="element-223" data-mp="3uep0vztfuxhn">
              <li className="flex items-start gap-2.5 text-ink-muted" data-id="element-224" data-mp="3qclnh5n7litq">
                <MapPinIcon size={17} className="mt-0.5 shrink-0 text-accent-600" aria-hidden data-id="element-225" data-mp="qwotomvpiw53" />
                <span data-id="element-226" data-mp="lwka2nzi5lan">{site.adresse}</span>
              </li>
              <li data-id="element-227" data-mp="lz5bjbvw1z7v">
                <a href={`mailto:${site.email}`} className="inline-flex items-center gap-2.5 text-ink-muted hover:text-brand-800" data-id="element-228" data-mp="h6c5mkoooku7">
                  <MailIcon size={17} className="text-accent-600" aria-hidden data-id="element-229" data-mp="d2xqdw8kk8ht" /> {site.email}
                </a>
              </li>
              <li data-id="element-230" data-mp="14vemhqbhu9nn">
                <a href={`tel:${site.telephoneLien}`} className="inline-flex items-center gap-2.5 text-ink-muted hover:text-brand-800" data-id="element-231" data-mp="14uvep2fk51nc">
                  <PhoneIcon size={17} className="text-accent-600" aria-hidden data-id="element-232" data-mp="3434vmwop413z" /> {site.telephone}
                </a>
              </li>
              <li data-id="element-233" data-mp="2pdp6fl4tc3qe">
                <a href={site.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2.5 text-ink-muted hover:text-brand-800" data-id="element-234" data-mp="2pc4m0oc7q169">
                  <InstagramIcon size={17} className="text-accent-600" aria-hidden data-id="element-235" data-mp="2ldf0u9kxnfc2" />{' '}
                  @zillenium_academy
                </a>
              </li>
            </ul>
          </div>

          <div className="rounded-xl border border-surface-line bg-white p-6" data-id="element-236" data-mp="2bdeh4mj5ix7c">
            <h2 className="text-base font-bold text-ink" data-id="element-237" data-mp="4nzv3i3bj02g">Besoin d’un aménagement ?</h2>
            <p className="mt-2 text-[15px] leading-relaxed text-ink-muted" data-id="element-238" data-mp="9muikxc8dcsw">
              Zillenium Academy étudie les aménagements possibles en fonction des besoins du
              bénéficiaire.
            </p>
            <a href={`mailto:${site.refHandicap}`} className="mt-3 inline-block text-sm font-semibold text-accent-600 hover:underline" data-id="element-239" data-mp="1p6anm1ape2gt">
              Référent handicap : {site.refHandicap}
            </a>
          </div>
        </aside>
      </div>
    </>;
}